import { Component, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';

/**
 * Component for a sidebar button that can either navigate to a route or trigger a click event.
 * This component is typically used within a sidebar for navigation or actions.
 * It supports both routing via `routerLink` and click events.
 * Example usage:
 * ```html
 * <lvr-sidebar-button
 *   [route]="['/some-route']"
 *   icon="home"
 *   label="Home"
 * ></lvr-sidebar-button>
 * <lvr-sidebar-button
 *   click="someMethod()"
 *   icon="settings"
 *   label="Settings"
 * ></lvr-sidebar-button>
 * ```
 */
@Component({
	selector: 'lvr-sidebar-button',
	template: `
		@if (route()) {
			<a mat-button [class]="isActive() ? 'active' : ''" [routerLink]="route()">
				<mat-icon [fontIcon]="icon()"></mat-icon>
				{{ label() }}
			</a>
		} @else {
			<button mat-button [class]="isActive() ? 'active' : ''" (click)="selected.emit()">
				<mat-icon [fontIcon]="icon()"></mat-icon>
				{{ label() }}
			</button>
		}
	`,
	styleUrl: './sidebar.scss',
	imports: [MatButtonModule, RouterModule, MatIconModule],
})
export class SidebarButton {
	/**
	 * The route to navigate to when the button is clicked.
	 * If this is set, the button will render as a link.
	 */
	public readonly route = input<string[]>();
	/**
	 * The icon to display in the button.
	 * Uses Material Icons by default.
	 */
	public readonly icon = input<string>('question_mark');
	/**
	 * The label to display next to the icon in the button.
	 */
	public readonly label = input<string>('LABEL_TO_BE_SET');
	/**
	 * Indicates whether the button is currently active.
	 * This can be used to style the button differently when active.
	 */
	public readonly isActive = input<boolean>(false);
	/**
	 * On click of the button if no route is defined.
	 */
	public readonly selected = output<void>();
}
